import React from "react";
import { ExternalLink, Award, Sparkles, ShieldCheck } from "lucide-react";
import { BRAND, IMAGES } from "../lib/site";
import { PageHero, SectionHeading } from "../components/Sections";

const pillars = [
  {
    icon: Award,
    title: "Surgical Mastery",
    desc: "Decades of body contouring artistry, refined through fellowship-trained precision and continued aesthetic study.",
  },
  {
    icon: ShieldCheck,
    title: "Uncompromised Safety",
    desc: "Accredited operating suites, board-certified anesthesia and stringent recovery protocols at every stage.",
  },
  {
    icon: Sparkles,
    title: "Bespoke Aesthetic",
    desc: "Every silhouette is composed in private dialogue — measured, sketched and confirmed in 3D imaging.",
  },
];

export default function AboutPage() {
  return (
    <div data-testid="page-about">
      <PageHero
        kicker="About the House"
        title="A private atelier for"
        accent="aesthetic transformation"
        subtitle="Founded on the belief that beauty deserves both science and ceremony, Medical Arts Groves Inc is a private sanctuary — with a main office in Raleigh, NC and headquarters in Casper, WY — devoted to surgical artistry and resort-grade recovery."
        image={IMAGES.floral}
        testid="about-hero"
      />

      {/* Story */}
      <section className="py-24 md:py-32 bg-mag">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <SectionHeading kicker="Our Story" title="Crafting confidence" accent="quietly, exquisitely" testid="about-story-heading" />
            <p className="text-white/75 font-heading text-lg mt-8 leading-relaxed">
              Medical Arts Groves Inc was founded for the woman who has
              outgrown the cold, clinical model of aesthetic medicine. Here,
              consultations unfold over candlelight and herbal tea.
              Operating theatres are private. Recovery is an act of indulgence.
            </p>
            <p className="text-white/70 font-heading text-lg mt-5 leading-relaxed">
              We specialise in Brazilian Butt Lift, breast enhancement and
              full-spectrum body sculpting — paired with our concierge
              medical recovery center for a seamless, deeply personal journey.
            </p>
          </div>
          <div className="md:col-span-6">
            <img src={IMAGES.abstractGold} alt="" className="w-full h-[520px] object-cover" />
          </div>
        </div>
      </section>

      {/* NPI */}
      <section className="py-20 bg-mag-2 border-y border-[rgba(212,175,55,0.2)]">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <span className="text-[11px] tracking-[0.4em] uppercase text-gold font-body">Verified Credentials</span>
          <h3 className="font-heading text-4xl md:text-5xl text-white mt-4">
            NPI · National Provider Identifier
          </h3>
          <p className="text-white/65 mt-4 max-w-2xl mx-auto">
            Our practice is registered with the official Centers for Medicare
            &amp; Medicaid Services NPI Registry. Click below to verify our
            provider record.
          </p>
          <a
            href={BRAND.npiUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="about-npi-link"
            className="btn-gold mt-8"
          >
            NPI {BRAND.npi} <ExternalLink size={14} />
          </a>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-24 md:py-32 bg-mag">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading kicker="The House Standards" title="Three pillars of" accent="our practice" align="center" testid="about-pillars-heading" />
          <div className="grid md:grid-cols-3 gap-6 md:gap-8 mt-16">
            {pillars.map((p, i) => (
              <div key={i} className="mag-card p-10" data-testid={`about-pillar-${i}`}>
                <p.icon size={28} className="text-gold mb-6" />
                <h4 className="font-heading text-2xl text-white mb-3">{p.title}</h4>
                <p className="text-white/65 text-[15px] leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
